<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header__icon"><i class="fa fa-envelope-o"></i></div>
        <div class="panel-header__text">
          <div class="panel-header__title">Email Whitelist</div>
          <div class="panel-header__sub">Manage whitelisted email addresses and contacts</div>
        </div>
        <div class="panel-header__action" v-if="currentUser.u.roles === 'admin'">
          <router-link :to="{ name: 'client.whitelist.create' }" class="btn-add">
            <i class="fa fa-plus"></i> Add
          </router-link>
        </div>
      </div>

      <!-- Toolbar -->
      <div class="toolbar">
        <div class="search-wrap">
          <i class="fa fa-search search-icon"></i>
          <input v-model="searchInput" type="text" class="search-input" placeholder="Search by company or email…" />
        </div>
        <div class="toolbar__meta" v-if="!loadingData">
          {{ filteredList.length }} {{ filteredList.length === 1 ? 'entry' : 'entries' }}
        </div>
      </div>

      <!-- Loading -->
      <div class="state-box" v-if="loadingData">
        <div class="state-spinner"></div>
        <span>Loading whitelist…</span>
      </div>

      <!-- Empty -->
      <div class="state-box state-box--muted" v-else-if="filteredList.length === 0">
        <i class="fa fa-envelope-o state-box__icon"></i>
        <div class="state-box__title">No entries found</div>
        <div class="state-box__sub">{{ searchInput ? 'Try a different search term' : 'Add the first whitelisted email to
          get started' }}</div>
      </div>

      <!-- Table -->
      <div class="table-wrap" v-else>
        <table class="wl-table">
          <thead>
            <tr>
              <th class="th-sm">#</th>
              <th class="th-company">Company</th>
              <th class="th-contact">Contact Name</th>
              <th class="th-email">Email</th>
              <th class="th-action">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in filteredList" :key="item.id || index">
              <td class="td-sm">{{ index + 1 }}</td>
              <td class="td-company">{{ item.company_name }}</td>
              <td class="td-contact">{{ item.contact_name }}</td>
              <td class="td-email">
                <span class="email-badge"><i class="fa fa-envelope-o"></i> {{ item.email }}</span>
              </td>
              <td class="td-action">
                <button class="dots-btn" @click.stop="toggleMenu(index, $event)">
                  <i class="fa fa-ellipsis-v"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>

    <!-- 3-dot dropdown -->
    <div class="dot-menu" v-if="activeMenu !== null" :style="{ top: menuPos.top + 'px', right: menuPos.right + 'px' }"
      @click.stop>
      <button class="dot-item" @click="edit(menuItem); activeMenu = null">
        <i class="fa fa-pencil-alt"></i> Edit
      </button>
      <button class="dot-item dot-item--del" @click="openDeleteModal(menuItem); activeMenu = null">
        <i class="fa fa-trash"></i> Delete
      </button>
    </div>

    <!-- Delete modal -->
    <transition name="modal-fade">
      <div class="del-overlay" v-if="deleteModal.show" @click.self="deleteModal.show = false">
        <div class="del-box">
          <div class="del-icon"><i class="fa fa-trash"></i></div>
          <div class="del-title">Delete Entry</div>
          <div class="del-sub">
            This will permanently remove <strong>{{ deleteModal.email }}</strong> from the whitelist.
          </div>
          <div class="del-actions">
            <button class="del-cancel" @click="deleteModal.show = false">Cancel</button>
            <button class="del-confirm" @click="confirmDelete">Delete</button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import {
  GET_WHITELIST_EMAIL_LIST,
  DELETE_WHITELIST_EMAIL,
} from "@/core/services/store/actions.type";
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import { mapGetters } from "vuex";

export default {
  computed: {
    ...mapGetters(["currentUser"]),
    filteredList() {
      const q = this.searchInput.toLowerCase();
      if (!q) return this.whitelistemail;
      return this.whitelistemail.filter(i =>
        (i.company_name || "").toLowerCase().includes(q) ||
        (i.email || "").toLowerCase().includes(q) ||
        (i.contact_name || "").toLowerCase().includes(q)
      );
    },
  },
  data() {
    return {
      loadingData: false,
      whitelistemail: [],
      searchInput: "",
      activeMenu: null,
      menuPos: { top: 0, right: 0 },
      menuItem: null,
      deleteModal: { show: false, id: null, email: "" },
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Setting", route: "" },
        { id: 3, title: "Email WhiteList", route: "" },
      ],
    };
  },
  mounted() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.loadingData = true;
    this.$store.dispatch(GET_WHITELIST_EMAIL_LIST).then(res => {
      this.whitelistemail = res.data;
      this.loadingData = false;
    }).catch(() => { this.loadingData = false; });
    document.addEventListener("click", this.closeMenu);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeMenu);
  },
  methods: {
    edit(item) {
      this.$router.push({
        name: "client.whitelist.edit",
        params: { client_slug: this.$route.params.client_slug, id: item.id },
      });
    },
    toggleMenu(index, event) {
      if (this.activeMenu === index) { this.activeMenu = null; return; }
      const r = event.currentTarget.getBoundingClientRect();
      this.menuPos = { top: r.bottom + 4, right: window.innerWidth - r.right };
      this.menuItem = { ...this.filteredList[index] };
      this.activeMenu = index;
    },
    closeMenu() { this.activeMenu = null; },
    openDeleteModal(item) {
      this.deleteModal = { show: true, id: item.id, email: item.email };
    },
    confirmDelete() {
      const id = this.deleteModal.id;
      this.deleteModal.show = false;
      this.$store.dispatch(DELETE_WHITELIST_EMAIL, id).then(() => {
        this.$toastr.s("Email Successfully Deleted");
        this.$store.dispatch(GET_WHITELIST_EMAIL_LIST).then(res => {
          this.whitelistemail = res.data;
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
$font: 'Segoe UI', sans-serif;
$blue: #389ad4;
$dark: #0d1b3e;
$border: #dde2ec;

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.page-wrap {
  padding: 12px;
  font-family: $font;
}

/* ── panel card ── */
.panel-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 16px;
  overflow: clip;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

/* ── header ── */
.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid $border;
  background: linear-gradient(135deg, #f8faff 0%, #f0f5ff 100%);

  &__icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, $blue, #2277aa);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 18px;
    flex-shrink: 0;
  }

  &__text {
    flex: 1;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: $dark;
    line-height: 1.2;
  }

  &__sub {
    font-size: 12px;
    color: #7a8499;
    margin-top: 3px;
  }

  &__action {
    display: flex;
    align-items: center;
  }
}

.btn-add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 16px;
  border-radius: 8px;
  background: $blue;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  text-decoration: none;
  border: none;
  white-space: nowrap;
  transition: background 0.15s, box-shadow 0.15s;

  &:hover {
    background: #2d85bb;
    box-shadow: 0 4px 12px rgba(56, 154, 212, 0.35);
    color: #fff;
    text-decoration: none;
  }

  i {
    font-size: 11px;
  }
}

/* ── toolbar ── */
.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border-bottom: 1px solid $border;
  background: #fafbfd;

  &__meta {
    margin-left: auto;
    font-size: 12px;
    color: #7a8499;
  }
}

.search-wrap {
  position: relative;
  width: 300px;
}

.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aab8;
  font-size: 12px;
  pointer-events: none;
}

.search-input {
  width: 100%;
  height: 34px;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 0 10px 0 30px;
  font-size: 13px;
  color: $dark;
  font-family: $font;
  outline: none;
  background: #fff;
  transition: border-color 0.2s;

  &:focus {
    border-color: $blue;
    box-shadow: 0 0 0 3px rgba(56, 154, 212, 0.1);
  }

  &::placeholder {
    color: #b0bac9;
  }
}

/* ── state boxes ── */
.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 24px;
  gap: 8px;
  color: #7a8499;
  font-size: 13px;

  &--muted .state-box__icon {
    color: #c5d0e0;
  }

  &__icon {
    font-size: 36px;
    margin-bottom: 4px;
  }

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: $dark;
  }

  &__sub {
    font-size: 13px;
  }
}

.state-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #c5d8f5;
  border-top-color: $blue;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin-bottom: 8px;
}

/* ── table ── */
.table-wrap {
  overflow-x: auto;
  overflow-y: auto;
  max-height: calc(100vh - 220px);
}

.wl-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: $dark;

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 10px 16px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #7a8499;
    border-bottom: 2px solid $border;
    white-space: nowrap;
    background: #fafbfd;
  }

  .th-sm {
    width: 52px;
    text-align: center;
  }

  .th-company {
    width: 22%;
  }

  .th-contact {
    width: 20%;
  }

  .th-email {
    width: 30%;
  }

  .th-action {
    width: 60px;
    text-align: center;
  }

  td {
    padding: 11px 16px;
    border-bottom: 1px solid #f0f2f8;
    vertical-align: middle;
  }

  tr:hover td {
    background: #f8fbff;
  }

  tr:last-child td {
    border-bottom: none;
  }

  .td-sm {
    text-align: center;
    color: #a0aab8;
    font-size: 12px;
  }

  .td-company {
    font-weight: 500;
  }

  .td-contact {
    color: #5a6278;
    font-size: 12.5px;
  }

  .td-action {
    text-align: center;
  }
}

.email-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: $blue;
  background: rgba(56, 154, 212, 0.08);
  padding: 3px 10px;
  border-radius: 20px;
  font-weight: 500;

  i {
    font-size: 11px;
  }
}

/* ── 3-dot ── */
.dots-btn {
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: #a0aab8;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #f0f3fa;
    color: $dark;
  }
}

.dot-menu {
  position: fixed;
  z-index: 9999;
  background: #fff;
  border: 1.5px solid $border;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(13, 27, 62, 0.12);
  padding: 4px;
  min-width: 130px;
}

.dot-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: transparent;
  font-size: 13px;
  font-family: $font;
  color: $dark;
  cursor: pointer;
  border-radius: 7px;
  text-align: left;
  transition: background 0.12s;

  i {
    font-size: 12px;
    color: #7a8499;
  }

  &:hover {
    background: #f4f6fb;
  }

  &--del {
    color: #b42318;

    i {
      color: #b42318;
    }

    &:hover {
      background: rgba(180, 35, 24, 0.07);
    }
  }
}

/* ── delete modal ── */
.del-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(13, 27, 62, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.del-box {
  background: #fff;
  border-radius: 16px;
  padding: 32px 28px 24px;
  width: 380px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(13, 27, 62, 0.2);
}

.del-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(180, 35, 24, 0.1);
  color: #b42318;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
}

.del-title {
  font-size: 16px;
  font-weight: 700;
  color: $dark;
  margin-bottom: 8px;
}

.del-sub {
  font-size: 13px;
  color: #7a8499;
  line-height: 1.5;
  margin-bottom: 24px;

  strong {
    color: $dark;
  }
}

.del-actions {
  display: flex;
  gap: 10px;
}

.del-cancel {
  flex: 1;
  height: 38px;
  border-radius: 8px;
  border: 1.5px solid $border;
  background: #fff;
  color: $dark;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;

  &:hover {
    background: #f4f6fb;
  }
}

.del-confirm {
  flex: 1;
  height: 38px;
  border-radius: 8px;
  border: none;
  background: #b42318;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;

  &:hover {
    background: #8f1a12;
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
